import {
  useCallback,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react"

interface EntityPickerDraft {
  /** Whether the picker dialog is open. */
  isOpen: boolean
  /** The in-dialog selection, discarded unless the user applies it. */
  draftIds: string[]
  setDraftIds: Dispatch<SetStateAction<string[]>>
  /** Seeds the draft from the committed selection and opens the dialog. */
  open: () => void
  /** Commits the draft through `onCommit` and closes. */
  apply: () => void
  /** Closes, leaving the committed selection untouched. */
  cancel: () => void
}

/**
 * The draft a picker dialog edits, held by the card that owns the committed
 * selection. Editing a copy is what lets Cancel mean cancel; the card keeps it
 * rather than the dialog so it can read the draft while the dialog is open —
 * to work out which rows a selection rule has just ruled out, say.
 */
export function useEntityPickerDraft(
  committedIds: string[],
  onCommit: (ids: string[]) => void,
  { disabled = false }: { disabled?: boolean } = {},
): EntityPickerDraft {
  const [isOpen, setIsOpen] = useState(false)
  const [draftIds, setDraftIds] = useState<string[]>([])

  const open = useCallback(() => {
    if (disabled) {
      return
    }
    setDraftIds([...committedIds])
    setIsOpen(true)
  }, [committedIds, disabled])

  const apply = useCallback(() => {
    onCommit([...draftIds])
    setIsOpen(false)
  }, [draftIds, onCommit])

  const cancel = useCallback(() => {
    setIsOpen(false)
  }, [])

  return { isOpen, draftIds, setDraftIds, open, apply, cancel }
}
